import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API, formatApiError } from "../auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Shield } from "lucide-react";

export default function AdminUsers() {
  const { authHeaders, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", position: "", role: "manager", brands: [], can_trade: false, can_bandeo: false, can_maquillaje: false });
  const [saving, setSaving] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [creatingBrand, setCreatingBrand] = useState(false);

  const loadBrands = async () => {
    try {
      const r = await axios.get(`${API}/brands`, { headers: authHeaders() });
      setBrands(r.data);
      return r.data;
    } catch { return []; }
  };

  const createBrandInline = async () => {
    const name = newBrandName.trim();
    if (!name) return;
    setCreatingBrand(true);
    try {
      await axios.post(`${API}/brands`, { name, description: "" }, { headers: authHeaders() });
      const list = await loadBrands();
      setForm((f) => list.find((b) => b.name === name) ? { ...f, brands: Array.from(new Set([...f.brands, name])) } : f);
      setNewBrandName("");
      toast.success(`Marca "${name}" creada`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setCreatingBrand(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`, { headers: authHeaders() });
      setUsers(data);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    loadBrands();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", password: "", name: "", position: "", role: "manager", brands: [], can_trade: false, can_bandeo: false, can_maquillaje: false });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, position: u.position || "", role: u.role, brands: u.brands || [], can_trade: u.can_trade === true, can_bandeo: u.can_bandeo === true, can_maquillaje: u.can_maquillaje === true });
    setOpen(true);
  };

  const toggleBrand = (name) => {
    setForm((f) => f.brands.includes(name) ? { ...f, brands: f.brands.filter((x) => x !== name) } : { ...f, brands: [...f.brands, name] });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        position: form.position,
        role: form.role,
        brands: form.brands,
        can_trade: form.can_trade,
        can_bandeo: form.can_bandeo,
        can_maquillaje: form.can_maquillaje,
      };
      if (!editing) {
        payload.email = form.email;
        payload.password = form.password;
        await axios.post(`${API}/users`, payload, { headers: authHeaders() });
        toast.success("Usuario creado");
      } else {
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/users/${editing.id}`, payload, { headers: authHeaders() });
        toast.success("Usuario actualizado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  const del = async (u) => {
    try {
      await axios.delete(`${API}/users/${u.id}`, { headers: authHeaders() });
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="admin-users-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="kbd-label mb-2 text-emerald-700">/ Administración</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Usuarios</h1>
          <p className="text-slate-500 mt-2">Personas que cargan reportes en el sistema.</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" data-testid="create-user-btn">
          <Plus className="w-4 h-4" /> Nuevo usuario
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 kbd-label text-slate-400 text-center">Cargando…</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {users.map((u) => (
              <li key={u.id} className="p-4 md:px-6 flex flex-col md:flex-row md:items-center gap-3 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-display font-bold ${u.role === "admin" ? "bg-emerald-700 text-white" : "bg-slate-200 text-slate-700"}`}>
                    {u.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-950 flex items-center gap-2 flex-wrap">
                      {u.name}
                      {u.role === "admin" && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded">
                          <Shield className="w-3 h-3" /> Admin
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 truncate">
                      {u.position || "—"} · {u.email}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 md:max-w-xs">
                  {(u.brands || []).map((b) => (
                    <span key={b} className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 px-2 py-1 rounded">{b}</span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)} className="gap-2" data-testid={`edit-user-${u.id}`}>
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </Button>
                  {u.id !== me?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2 text-red-700 border-red-200 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Eliminar
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar a {u.name}?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(u)} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>
              {editing ? "Actualiza los datos del usuario." : "Crea una cuenta para una nueva persona del equipo."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="kbd-label">Nombre completo</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="user-name" />
            </div>
            <div>
              <Label className="kbd-label">Cargo</Label>
              <Input
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="mt-2"
                placeholder="Ej: Gerente de marca, Supervisor, Merchandiser, Director comercial"
                data-testid="user-position"
              />
            </div>
            <div>
              <Label className="kbd-label">Email</Label>
              <Input type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="user-email" />
            </div>
            <div>
              <Label className="kbd-label">Contraseña {editing && <span className="normal-case tracking-normal">(dejar en blanco para no cambiar)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2" data-testid="user-password" />
            </div>
            <div>
              <Label className="kbd-label">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-2" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Usuario estándar</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Acceso a módulos */}
            <div className="space-y-2">
              <div className="kbd-label">Acceso a módulos</div>
              <div className="bg-emerald-50/50 border border-emerald-200 rounded-md p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.can_trade}
                    onChange={(e) => setForm({ ...form, can_trade: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-emerald-700"
                    data-testid="user-can-trade"
                  />
                  <div className="flex-1">
                    <div className="kbd-label !text-emerald-800">Acceso al módulo Trade</div>
                    <p className="text-xs text-slate-600 mt-1">Permite cargar reportes de visita en punto de venta.</p>
                  </div>
                </label>
              </div>
              <div className="bg-emerald-50/50 border border-emerald-200 rounded-md p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.can_bandeo}
                    onChange={(e) => setForm({ ...form, can_bandeo: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-emerald-700"
                    data-testid="user-can-bandeo"
                  />
                  <div className="flex-1">
                    <div className="kbd-label !text-emerald-800">Acceso al módulo Bandeo</div>
                    <p className="text-xs text-slate-600 mt-1">
                      Permite registrar bandeos en cadenas.
                    </p>
                  </div>
                </label>
              </div>
              <div className="bg-emerald-50/50 border border-emerald-200 rounded-md p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.can_maquillaje}
                    onChange={(e) => setForm({ ...form, can_maquillaje: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-emerald-700"
                    data-testid="user-can-maquillaje"
                  />
                  <div className="flex-1">
                    <div className="kbd-label !text-emerald-800">Acceso al módulo Diagnóstico de Maquillaje</div>
                    <p className="text-xs text-slate-600 mt-1">
                      Permite llenar fichas de diagnóstico de maquillaje.
                    </p>
                  </div>
                </label>
              </div>
              <p className="text-[11px] text-slate-500">Los administradores siempre tienen acceso a todos los módulos.</p>
            </div>
            <div>
              <Label className="kbd-label">Marcas asignadas</Label>
              {brands.length === 0 ? (
                <p className="text-xs text-slate-500 mt-2">Aún no hay marcas. Crea la primera abajo ↓</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {brands.map((b) => {
                    const active = form.brands.includes(b.name);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => toggleBrand(b.name)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-600"}`}
                        data-testid={`brand-toggle-${b.id}`}
                      >
                        {b.name}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Inline quick create brand */}
              <div className="mt-3 flex gap-2 bg-slate-50 border border-dashed border-slate-300 rounded-md p-2">
                <Input
                  placeholder="Nueva marca (p.ej. Coca-Cola)"
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createBrandInline(); } }}
                  className="h-9 text-sm"
                  data-testid="inline-new-brand-input"
                />
                <Button
                  type="button"
                  onClick={createBrandInline}
                  disabled={creatingBrand || !newBrandName.trim()}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white h-9 px-3 gap-1 shrink-0"
                  data-testid="inline-create-brand-btn"
                >
                  <Plus className="w-4 h-4" /> Agregar
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Las marcas aparecen como etiquetas arriba. Haz clic para asignar/desasignar al usuario.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-user-btn">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
